
import { Email, EmailStatistics } from '../types';
import { useEmailDatabaseOperations } from './useEmailDatabaseOperations';
import { useEmailCrudOperations } from './useEmailCrudOperations';
import { useEmailFolderOperations } from './useEmailFolderOperations';
import { useEmailCompositionOperations } from './useEmailCompositionOperations';
import { useEmailSyncOperations } from './useEmailSyncOperations';
import { listDrafts } from '@/services/api/email';

interface UseEmailOperationsProps {
  emails: Email[];
  setEmails: (emails: Email[]) => void;
  setCurrentEmail: (email: Email | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: boolean) => void;
  setStatistics: (stats: EmailStatistics) => void;
  setHasMoreEmails: (hasMore: boolean) => void;
  setTotalEmailsInFolder: (total: number) => void;
  setTotalPages: (pages: number) => void;
  currentFolder: string;
}

export const useEmailOperations = ({
  emails,
  setEmails,
  setCurrentEmail,
  setLoading,
  setError,
  setStatistics,
  setHasMoreEmails,
  setTotalEmailsInFolder,
  setTotalPages,
  currentFolder,
}: UseEmailOperationsProps) => {

  // Database and statistics operations
  const databaseOps = useEmailDatabaseOperations({
    setStatistics,
    setError,
  });

  // CRUD operations
  const crudOps = useEmailCrudOperations({
    emails,
    setEmails,
    setCurrentEmail,
    setLoading,
    setError,
    setHasMoreEmails,
    setTotalEmailsInFolder,
    setTotalPages,
  });

  // Folder operations
  const folderOps = useEmailFolderOperations({
    emails,
    setEmails,
  });

  // Composition operations
  const compositionOps = useEmailCompositionOperations();

  // Sync operations - now pass currentFolder
  const syncOps = useEmailSyncOperations({
    fetchEmails: crudOps.fetchEmails,
    loadStatistics: databaseOps.loadStatistics,
    currentFolder, // Pass the current folder from context
  });

  return {
    // Database operations
    initializeDatabase: databaseOps.initializeDatabase,
    loadStatistics: databaseOps.loadStatistics,
    
    // CRUD operations
    fetchEmails: crudOps.fetchEmails,
    getEmail: crudOps.getEmail,
    markAsRead: crudOps.markAsRead,
    markAsUnread: crudOps.markAsUnread,
    
    // Folder operations
    moveToTrash: folderOps.moveToTrash,
    moveToFolder: folderOps.moveToFolder,
    deleteEmail: folderOps.permanentlyDeleteEmail,
    permanentlyDeleteEmail: folderOps.permanentlyDeleteEmail,
    
    // Composition operations
    sendEmail: compositionOps.sendEmail,
    replyToEmail: compositionOps.replyToEmail,
    saveDraft: compositionOps.saveDraft,
    loadDraft: compositionOps.loadDraft,
    sendDraft: compositionOps.sendDraft,
    listDrafts: async () => {
      try {
        const response = await listDrafts();
        return response.data || [];
      } catch (error) {
        console.error('Failed to list drafts:', error);
        return [];
      }
    },
    
    // Sync operations
    syncWithServer: syncOps.syncWithServer,
    refreshEmails: syncOps.refreshEmails,
  };
};
